import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { ModeSelector } from './ModeSelector';
import { useT } from '../i18n';
import { useSettingsStore } from '../stores/settingsStore';

export const PersonalizationPanel = ({
  currentMode, onModeChange,
  userContext, onUserContextChange,
  mapContext, onMapContextChange,
  onImproveUserContext,
  onImproveMapContext,
}) => {
  const t = useT();
  const lang = useSettingsStore((s) => s.lang);
  const setLang = useSettingsStore((s) => s.setLang);
  const theme = useSettingsStore((s) => s.theme);
  const setTheme = useSettingsStore((s) => s.setTheme);
  const [improving, setImproving] = useState({ user: false, map: false });

  const handleImprove = async (type) => {
    const onImprove = type === 'user' ? onImproveUserContext : onImproveMapContext;
    if (!onImprove) return;
    setImproving(prev => ({ ...prev, [type]: true }));
    try {
      await onImprove();
    } finally {
      setImproving(prev => ({ ...prev, [type]: false }));
    }
  };

  return (
    <div className="px-5 pb-4 pt-3 flex flex-col gap-4 animate-[fadeIn_0.2s_ease-out] shrink-0 max-h-[min(42vh,30rem)] overflow-y-auto overscroll-contain" style={{ borderBottom: '1px solid var(--border-s)', background: 'var(--surface)' }}>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--text-dim)' }}>{t('personalization.modeLabel')}</div>
        <ModeSelector currentMode={currentMode} onModeChange={onModeChange} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-dim)' }}>{t('personalization.profileLabel')}</div>
          <button
            onClick={() => handleImprove('user')}
            disabled={!userContext.trim() || improving.user}
            title={t('personalization.improveTitle')}
            className="flex items-center gap-1 text-[11px] disabled:cursor-not-allowed transition-colors"
            style={{ color: 'var(--accent-2)' }}
          >
            <Sparkles size={12} className={improving.user ? 'animate-pulse' : ''} />
            {improving.user ? t('personalization.improving') : t('personalization.improve')}
          </button>
        </div>
        <div className="text-[11px] mb-2" style={{ color: 'var(--text-dim)' }}>{t('personalization.profileHint')}</div>
        <textarea
          value={userContext}
          onChange={(e) => onUserContextChange(e.target.value)}
          disabled={improving.user}
          placeholder={t('personalization.profilePlaceholder')}
          rows={4}
          className="w-full px-4 py-3 text-[13px] focus:outline-none rounded-[14px] resize-none transition-all disabled:opacity-60"
          style={{ color: 'var(--text-primary)', background: 'var(--bg)', border: '2px solid var(--border-s)' }}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-dim)' }}>{t('personalization.mapContextLabel')}</div>
          <button
            onClick={() => handleImprove('map')}
            disabled={!mapContext.trim() || improving.map}
            title={t('personalization.improveTitle')}
            className="flex items-center gap-1 text-[11px] disabled:cursor-not-allowed transition-colors"
            style={{ color: 'var(--accent-2)' }}
          >
            <Sparkles size={12} className={improving.map ? 'animate-pulse' : ''} />
            {improving.map ? t('personalization.improving') : t('personalization.improve')}
          </button>
        </div>
        <div className="text-[11px] mb-2" style={{ color: 'var(--text-dim)' }}>{t('personalization.mapContextHint')}</div>
        <textarea
          value={mapContext}
          onChange={(e) => onMapContextChange(e.target.value)}
          disabled={improving.map}
          placeholder={t('personalization.mapContextPlaceholder')}
          rows={4}
          className="w-full px-4 py-3 text-[13px] focus:outline-none rounded-[14px] resize-none transition-all disabled:opacity-60"
          style={{ color: 'var(--text-primary)', background: 'var(--bg)', border: '2px solid var(--border-s)' }}
        />
      </div>

      {/* Language switcher */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--text-dim)' }}>{t('personalization.languageLabel')}</div>
        <div className="flex rounded-[10px] overflow-hidden w-fit" style={{ border: '2px solid var(--border-s)' }}>
          {['en', 'it'].map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className="px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider transition-all"
              style={lang === l
                ? { background: 'var(--accent-2)', color: 'var(--bg)' }
                : { color: 'var(--text-muted)' }}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--text-dim)' }}>{t('personalization.themeLabel')}</div>
        <div className="flex rounded-[10px] overflow-hidden w-fit" style={{ border: '2px solid var(--border-s)' }}>
          {[
            { id: 'mocha', label: t('personalization.themeDark') },
            { id: 'latte', label: t('personalization.themeLight') },
          ].map((option) => (
            <button
              key={option.id}
              onClick={() => setTheme(option.id)}
              className="px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider transition-all"
              style={theme === option.id
                ? { background: 'var(--accent-2)', color: 'var(--bg)' }
                : { color: 'var(--text-muted)' }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
